import { type UseModalState } from "@clipboard-health/ui-react";
import { Box, CircularProgress, Stack } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import {
  type Notification,
  useBrazeNotifications,
} from "@src/appV2/lib/Braze/useBrazeNotifications";
import { useCallback, useEffect } from "react";

import { Drawer } from "../../components/Drawer";
import { Icon } from "../../components/Icon";
import { NotificationCard } from "../../components/NotificationCard/NotificationCard";
import { NotificationList } from "../../components/NotificationCard/NotificationList";
import { Text } from "../../components/Text";
import { useBrazeInitializedProviderContext } from "./BrazeContext";

export interface NotificationsCenterDrawerProps {
  modalState: UseModalState;
}

export function NotificationsCenterDrawer(props: NotificationsCenterDrawerProps) {
  const { modalState } = props;
  const { admin } = useSession();

  const { brazeInitialized } = useBrazeInitializedProviderContext();
  const { notifications, isLoading, hasError, markAsClicked, dismiss, markAsDisplayed } =
    useBrazeNotifications(brazeInitialized);

  // Log notification IDs when drawer opens and notifications are loaded
  useEffect(() => {
    // make sure the notifications shows up as unseen for a facility, if an admin clicked on it
    if (!admin && modalState.modalIsOpen && notifications.length > 0) {
      const notificationIds = notifications.map((n) => n.id);
      markAsDisplayed(notificationIds);
    }
  }, [admin, modalState.modalIsOpen, notifications, markAsDisplayed]);

  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      // make sure the notifications shows up as unclicked for a facility, if an admin clicked on it
      if (!admin) {
        markAsClicked(notification.id);
      }
    },
    [admin, markAsClicked]
  );

  const handleNotificationDismiss = useCallback(
    (notification: Notification) => {
      dismiss(notification.id);
    },
    [dismiss]
  );

  const hasNotifications = notifications.length > 0;
  const showEmptyState = !isLoading && !hasNotifications;
  const showLoading = isLoading && !hasNotifications;

  return (
    <Drawer showCloseButton modalState={modalState} title="Notifications">
      <Box
        sx={{
          height: "100%",
          width: "100%",
        }}
      >
        {showLoading && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              position: "absolute",
              inset: 0,
            }}
          >
            <CircularProgress size={32} />
          </Stack>
        )}

        {hasError && (
          <Stack alignItems="center" justifyContent="center">
            <Text>Error Loading Notifications. Please try again later.</Text>
          </Stack>
        )}

        {showEmptyState && (
          <Stack alignItems="center" justifyContent="center" spacing={3} height="100%">
            <Icon type="bell-solid" size="large" sx={{ opacity: 0.3 }} />
            <Text
              variant="body1"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                textAlign: "center",
              })}
            >
              No current notifications
            </Text>
          </Stack>
        )}

        {hasNotifications && (
          <Box
            sx={{
              height: "100%",
              overflowY: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            <NotificationList>
              {notifications.map((notification) => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  onClick={handleNotificationClick}
                  onDismiss={handleNotificationDismiss}
                />
              ))}
            </NotificationList>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
